import React, { useEffect, useState, useRef } from "react";
import { shopifyExportApi } from "../../services/api/omni/shopify_export";

export default function ShopifyExportControl() {
  const [status, setStatus] = useState({
    todo: 0,
    done: 0,
    failed: 0,
    current: null,
    last_updated: null,
  });
  const [running, setRunning] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forceRebuild, setForceRebuild] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const { data } = await shopifyExportApi.getStatus();
      setStatus(data);
      const isActive = data.current !== null;
      setRunning(isActive);
      // Clear stopping flag once the worker has actually halted
      if (!isActive) setStopping(false);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch status. Is the service running?");
    }
  };

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, 5000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const start = async () => {
    try {
      setLoading(true);
      await shopifyExportApi.start(forceRebuild);
      await fetchStatus();
    } catch (err) {
      console.error(err);
      setError(String(err?.response?.data?.detail || err));
    } finally {
      setLoading(false);
    }
  };

  const stop = async () => {
    try {
      setLoading(true);
      await shopifyExportApi.stop();
      setStopping(true); // allow Start immediately; flag clears when current → null
      await fetchStatus();
    } catch (err) {
      console.error(err);
      setError(String(err?.response?.data?.detail || err));
    } finally {
      setLoading(false);
    }
  };

  const total = status.todo + status.done + status.failed;
  const pct = total > 0 ? Math.round((status.done / total) * 100) : 0;

  return (
    <div style={{ padding: 24, fontFamily: "sans-serif", maxWidth: 900 }}>
      <h2 style={{ marginTop: 0 }}>Shopify Export Control</h2>

      {error && (
        <div
          style={{
            marginBottom: 12,
            padding: 10,
            background: "#fff3f3",
            border: "1px solid #f5c6cb",
            borderRadius: 6,
            color: "#721c24",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 20,
          alignItems: "center",
        }}
      >
        <button
          onClick={start}
          disabled={loading || (running && !stopping)}
          style={{
            padding: "8px 16px",
            background: running && !stopping ? "#aaa" : "#1976d2",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor:
              loading || (running && !stopping) ? "not-allowed" : "pointer",
          }}
        >
          {running && !stopping ? "Running…" : "Start"}
        </button>
        <button
          onClick={stop}
          disabled={loading || !running || stopping}
          style={{
            padding: "8px 16px",
            background: !running || stopping ? "#aaa" : "#d32f2f",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor: loading || !running || stopping ? "not-allowed" : "pointer",
          }}
        >
          {stopping ? "Stopping…" : "Stop"}
        </button>
        <button
          onClick={fetchStatus}
          disabled={loading}
          style={{
            padding: "8px 16px",
            border: "1px solid #ccc",
            borderRadius: 6,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          Refresh
        </button>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginLeft: 8,
            userSelect: "none",
          }}
        >
          <input
            type="checkbox"
            checked={forceRebuild}
            onChange={(e) => setForceRebuild(e.target.checked)}
          />
          Force rebuild todo list
        </label>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#888" }}>
          Auto-refreshes every 5 s
        </span>
      </div>

      {/* Progress bar */}
      {total > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 12,
              color: "#666",
              marginBottom: 4,
            }}
          >
            <span>Progress</span>
            <span>
              {pct}% ({status.done} / {total})
            </span>
          </div>
          <div
            style={{
              background: "#eee",
              borderRadius: 999,
              height: 10,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${pct}%`,
                background: "#1976d2",
                height: "100%",
                transition: "width 0.4s",
              }}
            />
          </div>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <StatCard label="Todo" value={status.todo} color="#1976d2" />
        <StatCard label="Done" value={status.done} color="#388e3c" />
        <StatCard
          label="Failed / Deferred"
          value={status.failed}
          color="#d32f2f"
        />
        <div style={{ padding: 16, border: "1px solid #eee", borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
            Current item
          </div>
          <div
            style={{ fontSize: 14, fontWeight: "bold", wordBreak: "break-all" }}
          >
            {status.current ?? "—"}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#666",
              marginTop: 8,
              marginBottom: 2,
            }}
          >
            Last success
          </div>
          <div style={{ fontSize: 12 }}>
            {status.last_updated
              ? new Date(status.last_updated).toLocaleString()
              : "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ padding: 16, border: "1px solid #eee", borderRadius: 8 }}>
      <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: "bold", color }}>{value}</div>
    </div>
  );
}
